import { oc } from '@orpc/contract';
import * as z from 'zod';
import { youTubeVideoSchema } from '../domain/youtube.js';
import { contentTypeSchema, playerKindSchema, providerIdSchema } from '../enums/content.js';
import { childIdSchema, collectionIdSchema, contentItemIdSchema, familyIdSchema, } from '../primitives/id.js';
import { languageSchema } from '../primitives/language.js';
import { durationSecondsSchema, instantSchema } from '../primitives/time.js';
import { API_VERSION } from './version.js';
const content = `/${API_VERSION}/content`;
const collections = `/${API_VERSION}/collections`;
const playback = `/${API_VERSION}/playback`;
export const contentItemSchema = z.object({
    id: contentItemIdSchema,
    familyId: familyIdSchema,
    providerId: providerIdSchema,
    contentType: contentTypeSchema,
    /** What the parent calls it. Starts as the provider's title; theirs to change. */
    title: z.string().min(1).max(80),
    /**
     * The provider's own title, kept when the parent renames the item.
     *
     * YouTube titles for small children are written for an algorithm — emoji,
     * "NEW EPISODES", the channel's name twice — and a parent renames them. This is
     * what they renamed, so the panel can show where an item came from and offer
     * the way back. Null for items that never had one.
     */
    originalTitle: z.string().min(1).max(200).nullable(),
    artworkKey: z.string().min(1).max(40),
    /** A ready-to-load link to the item's own picture. See `contentTileSchema`. */
    posterUrl: z.url().max(500).nullable(),
    durationSeconds: durationSecondsSchema.nullable(),
    /**
     * What the child will hear, as reported by the download. Null when it is not
     * known: added by link, uploaded, or a track nobody labelled.
     */
    audioLanguage: languageSchema.nullable(),
    createdAt: instantSchema,
    /** Which children a parent has opened this to. Empty means nobody. */
    approvedForChildIds: z.array(childIdSchema),
});
export const collectionSchema = z.object({
    id: collectionIdSchema,
    familyId: familyIdSchema,
    title: z.string().min(1).max(40),
    artworkKey: z.string().min(1).max(40),
    /** A ready-to-load link to the channel's own mark. See `contentTileSchema`. */
    posterUrl: z.url().max(500).nullable(),
    /** In the order the child's tablet shows them. */
    itemIds: z.array(contentItemIdSchema),
    /** Where a newly imported item lands. See `domain/collection-order.ts`. */
    newItemsFirst: z.boolean(),
    /** Long compilations are kept below everything else. */
    longItemsLast: z.boolean(),
});
/**
 * How to play one item, for a short while.
 *
 * Provider credentials never appear here and never reach a device (SPEC.md §13,
 * §38). What travels is a URL that stops working shortly after it is issued, so
 * a descriptor copied off a tablet is worth nothing by the time it is used.
 */
export const playbackDescriptorSchema = z.object({
    kind: playerKindSchema,
    url: z.url(),
    headers: z.record(z.string(), z.string()).optional(),
    expiresAt: instantSchema,
    playerHint: z.enum(['NATIVE', 'PROVIDER_WEBVIEW']),
    /** Where this child left off, so playback can resume rather than restart. */
    resumePositionSeconds: durationSecondsSchema,
});
export const catalogContract = {
    list: oc
        .route({ method: 'GET', path: content })
        .output(z.object({ items: z.array(contentItemSchema) })),
    /**
     * Adds an item to the family's library.
     *
     * Adding is not approving. A new item reaches no child until a parent opens
     * it to one by name, which is default deny expressed as two separate actions
     * rather than a checkbox someone might leave ticked.
     */
    create: oc
        .route({ method: 'POST', path: content })
        .input(z.object({
        providerId: providerIdSchema,
        contentType: contentTypeSchema,
        title: z.string().min(1).max(80),
        artworkKey: z.string().min(1).max(40),
        sourceKey: z.string().min(1).max(500),
        durationSeconds: durationSecondsSchema.optional(),
    }))
        .output(contentItemSchema)
        .errors({
        PROVIDER_UNAVAILABLE: {
            status: 409,
            message: 'That provider is not enabled for this family',
        },
    }),
    remove: oc
        .route({ method: 'DELETE', path: `${content}/{contentItemId}` })
        .input(z.object({ contentItemId: contentItemIdSchema }))
        .output(z.object({ success: z.literal(true) }))
        .errors({ CONTENT_NOT_FOUND: { status: 404, message: 'Content not found' } }),
    /**
     * Renames an item.
     *
     * The provider's title stays in `originalTitle`; sending it back as `title`
     * is how a rename is undone.
     */
    rename: oc
        .route({ method: 'PATCH', path: `${content}/{contentItemId}` })
        .input(z.object({
        contentItemId: contentItemIdSchema,
        title: z.string().trim().min(1).max(80),
    }))
        .output(contentItemSchema)
        .errors({ CONTENT_NOT_FOUND: { status: 404, message: 'Content not found' } }),
    /** Opens an item to one child, or withdraws it. */
    setApproval: oc
        .route({ method: 'PUT', path: `${content}/{contentItemId}/approval` })
        .input(z.object({
        contentItemId: contentItemIdSchema,
        childId: childIdSchema,
        isApproved: z.boolean(),
    }))
        .output(contentItemSchema)
        .errors({
        CONTENT_NOT_FOUND: { status: 404, message: 'Content not found' },
        CHILD_NOT_FOUND: { status: 404, message: 'Child not found' },
    }),
};
export const youTubeContract = {
    /**
     * Resolves what a parent pasted into something they can look at.
     *
     * Read-only, and called from the Parent App by an adult. Kids Hub takes no
     * write action against YouTube — no uploads, no comments, no playlists — which
     * is the heaviest obligation the API terms place on a child-directed client
     * and one this product happens to meet by design (SPEC.md §11.2).
     *
     * The response carries the Made-for-Kids designation because embedding
     * requires knowing it, and whether the video is embeddable at all: an owner
     * can forbid embedding, and finding that out when a child taps the tile is
     * finding out too late.
     */
    resolve: oc
        .route({ method: 'POST', path: `/${API_VERSION}/providers/youtube/resolve` })
        .input(z.object({ url: z.string().min(1).max(500) }))
        .output(youTubeVideoSchema)
        .errors({
        INVALID_YOUTUBE_URL: { status: 400, message: 'That does not look like a YouTube video link' },
        VIDEO_NOT_FOUND: { status: 404, message: 'That video is unavailable' },
        NOT_EMBEDDABLE: {
            status: 409,
            message: 'The owner of that video does not allow it to be played outside YouTube',
        },
        PROVIDER_UNAVAILABLE: {
            status: 503,
            message: 'YouTube is not configured or is unreachable',
        },
    }),
};
export const collectionsContract = {
    list: oc
        .route({ method: 'GET', path: collections })
        .output(z.object({ collections: z.array(collectionSchema) })),
    create: oc
        .route({ method: 'POST', path: collections })
        .input(z.object({
        title: z.string().min(1).max(40),
        artworkKey: z.string().min(1).max(40),
    }))
        .output(collectionSchema),
    /**
     * Changes a collection's name or its ordering preferences.
     *
     * Switching `longItemsLast` on also applies it to what is already there:
     * a rule that only affected tomorrow's imports would look broken today.
     */
    update: oc
        .route({ method: 'PATCH', path: `${collections}/{collectionId}` })
        .input(z.object({
        collectionId: collectionIdSchema,
        title: z.string().trim().min(1).max(40).optional(),
        newItemsFirst: z.boolean().optional(),
        longItemsLast: z.boolean().optional(),
    }))
        .output(collectionSchema)
        .errors({ COLLECTION_NOT_FOUND: { status: 404, message: 'Collection not found' } }),
    /**
     * Puts the family's collections in the order a tablet's home screen shows.
     *
     * Every collection, once each: a partial list would leave the rest with
     * positions nobody chose.
     */
    reorder: oc
        .route({ method: 'PUT', path: `${collections}/order` })
        .input(z.object({ collectionIds: z.array(collectionIdSchema) }))
        .output(z.object({ collections: z.array(collectionSchema) }))
        .errors({ COLLECTION_NOT_FOUND: { status: 404, message: 'Collection not found' } }),
    setItems: oc
        .route({ method: 'PUT', path: `${collections}/{collectionId}/items` })
        .input(z.object({
        collectionId: collectionIdSchema,
        itemIds: z.array(contentItemIdSchema),
    }))
        .output(collectionSchema)
        .errors({
        COLLECTION_NOT_FOUND: { status: 404, message: 'Collection not found' },
        CONTENT_NOT_FOUND: { status: 404, message: 'Content not found' },
    }),
    remove: oc
        .route({ method: 'DELETE', path: `${collections}/{collectionId}` })
        .input(z.object({ collectionId: collectionIdSchema }))
        .output(z.object({ success: z.literal(true) }))
        .errors({ COLLECTION_NOT_FOUND: { status: 404, message: 'Collection not found' } }),
};
export const playbackContract = {
    /**
     * Asks to play something. Device-authenticated.
     *
     * Two refusals with different meanings. `CONTENT_NOT_APPROVED` says this child
     * may not have it at all; a gate reason says not now — the budget, the hours,
     * a rest. The child sees a friendly sentence either way, but a parent looking
     * at the same event needs to tell them apart.
     */
    resolve: oc
        .route({ method: 'POST', path: `${playback}/resolve` })
        .input(z.object({ contentItemId: contentItemIdSchema }))
        .output(z.discriminatedUnion('status', [
        z.object({ status: z.literal('ready'), descriptor: playbackDescriptorSchema }),
        z.object({ status: z.literal('refused'), reason: z.string() }),
    ]))
        .errors({
        CONTENT_NOT_FOUND: { status: 404, message: 'Content not found' },
        DEVICE_UNPAIRED: { status: 403, message: 'This device is no longer paired' },
    }),
    /** Records where a child got to, so the next attempt resumes. */
    reportProgress: oc
        .route({ method: 'POST', path: `${playback}/progress` })
        .input(z.object({
        contentItemId: contentItemIdSchema,
        positionSeconds: durationSecondsSchema,
    }))
        .output(z.object({ success: z.literal(true) }))
        .errors({ DEVICE_UNPAIRED: { status: 403, message: 'This device is no longer paired' } }),
};
//# sourceMappingURL=catalog.js.map